import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ConversionPreview } from "@/components/conversion-preview/ConversionPreview";
import {
  createWithdrawalSchema,
  WithdrawalFormValues,
} from "./schemas/withdrawalForm.schema";
import { useCreateSaccoWithdrawalWithCredit, useUpdateSaccoWithdrawal } from "@saccoHooks";
import { useFetchAccounts } from "@accountHooks";
import { useExchangeRates } from "@/hooks/useExchangeRates";
import { getDisplayName, disambiguateDisplayNames } from "@/lib/display-name";
import {SaccoWithdrawal} from "@/@types/sacco.models.ts";

interface WithdrawalFormProps {
  membershipId: string;
  nativeCurrency?: string;
  currentDepositBalance: number;
  withdrawal?: SaccoWithdrawal;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function WithdrawalForm({
  membershipId,
  nativeCurrency = "KES",
  currentDepositBalance,
  withdrawal,
  open,
  onOpenChange,
  onSuccess,
}: WithdrawalFormProps) {
  const isEdit = !!withdrawal;

  const createWithdrawal = useCreateSaccoWithdrawalWithCredit();
  const updateWithdrawal = useUpdateSaccoWithdrawal(membershipId);

  const { data: accounts, isLoading: isLoadingAccounts } = useFetchAccounts();
  const { getRate, isLoading: isLoadingRates } = useExchangeRates();

  const [selectedAccountId, setSelectedAccountId] = useState<string>("");

  // When editing, the effective balance includes the existing withdrawal amount
  const effectiveBalance = isEdit
    ? currentDepositBalance + Number(withdrawal.amount)
    : currentDepositBalance;

  const schema = createWithdrawalSchema(effectiveBalance, isEdit);

  const form = useForm<WithdrawalFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      amount: "" as unknown as number,
      withdrawal_date: "",
      note: "",
      target_account_id: "",
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        amount: withdrawal?.amount
          ? Number(withdrawal.amount)
          : ("" as unknown as number),
        withdrawal_date: withdrawal?.withdrawal_date ?? "",
        note: withdrawal?.note ?? "",
        target_account_id: "",
      });
      setSelectedAccountId("");
    }
  }, [open, withdrawal, form]);

  const isPending = createWithdrawal.isPending || updateWithdrawal.isPending;

  const selectedAccount = accounts?.find((a) => a.id === selectedAccountId);
  const targetAccountCurrency = selectedAccount?.currency ?? "";
  const amountValue = form.watch("amount");
  const parsedAmount = Number(amountValue) || 0;

  const showConversionPreview =
    !isEdit &&
    !!nativeCurrency &&
    !!targetAccountCurrency &&
    nativeCurrency !== targetAccountCurrency &&
    parsedAmount > 0;

  const disambiguatedAccounts = disambiguateDisplayNames(
    (accounts ?? []).map((a) => ({ ...a, displayName: getDisplayName(a) }))
  );

  async function onSubmit(values: WithdrawalFormValues) {
    try {
      if (isEdit) {
        await updateWithdrawal.mutateAsync({
          id: withdrawal.id,
          amount: values.amount,
          withdrawalDate: values.withdrawal_date,
          note: values.note || null,
        });
        toast.success("Withdrawal updated successfully");
      } else {
        await createWithdrawal.mutateAsync({
          membershipId,
          targetAccountId: values.target_account_id!,
          amount: values.amount,
          withdrawalCurrency: nativeCurrency,
          withdrawalDate: values.withdrawal_date,
          note: values.note || null,
        });
        toast.success("Withdrawal recorded successfully");
      }
      onOpenChange(false);
      onSuccess?.();
    } catch {
      toast.error(
        isEdit ? "Failed to update withdrawal" : "Failed to record withdrawal"
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Withdrawal" : "Add Withdrawal"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Create-only: Target account */}
            {!isEdit && (
              <FormField
                control={form.control}
                name="target_account_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Target Account *</FormLabel>
                    <FormControl>
                      <Select
                        disabled={isLoadingAccounts}
                        value={field.value ?? ""}
                        onValueChange={(val) => {
                          field.onChange(val);
                          setSelectedAccountId(val);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              isLoadingAccounts
                                ? "Loading accounts…"
                                : "Select target account"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {disambiguatedAccounts.map((account) => (
                            <SelectItem key={account.id} value={account.id}>
                              {account.displayName} ({account.currency})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    {selectedAccount && (
                      <p className="text-sm text-muted-foreground">
                        {selectedAccount.balance.toFixed(2)}{" "}
                        {selectedAccount.currency}
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{`Amount (${nativeCurrency}) *`}</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      inputMode="decimal"
                      placeholder="0.00"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* ConversionPreview */}
            {showConversionPreview && (
              <ConversionPreview
                amount={parsedAmount}
                fromCurrency={nativeCurrency}
                toCurrency={targetAccountCurrency}
                label="Estimated credit"
                getRate={getRate}
                isLoadingRates={isLoadingRates}
              />
            )}

            <FormField
              control={form.control}
              name="withdrawal_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Withdrawal Date *</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Note</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Optional note"
                      className="resize-none"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? "Saving..." : isEdit ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
